<template>
    <span v-if="props.status === ''" data-test="ten-dlc-campaign" class="text-slate-500">-</span>
    <div v-else-if="props.campaignId !== ''" class="flex min-w-0">
        <vx-popover hover teleport arrow placement="top" class="min-w-0">
            <div class="flex min-w-0 items-center gap-2">
                <vx-badge data-test="ten-dlc-campaign-status" :color="statusColor" size="xs" class="shrink-0">
                    {{ tenDlcCampaignStatusLabel(props.status) }}
                </vx-badge>
                <span data-test="ten-dlc-campaign" class="min-w-0 truncate">{{ campaignLabel }}</span>
            </div>
            <template v-slot:content>
                <div class="flex max-w-[280px] flex-col items-start gap-2">
                    <vx-badge data-test="ten-dlc-popover-status" :color="statusColor" size="xs">
                        {{ tenDlcCampaignStatusLabel(props.status) }}
                    </vx-badge>
                    <b>{{ campaignLabel }}</b>
                    <dl class="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                        <dt class="text-slate-500">Brand trust</dt>
                        <dd data-test="ten-dlc-brand-trust" class="m-0 font-semibold">
                            {{ detailText(brandTrust) }}
                        </dd>
                        <dt class="text-slate-500">Use case</dt>
                        <dd data-test="ten-dlc-use-case" class="m-0 font-semibold">
                            {{ detailText(useCaseLabel(props.useCase)) }}
                        </dd>
                        <dt class="text-slate-500">AT&amp;T class</dt>
                        <dd data-test="ten-dlc-att-class" class="m-0 font-semibold">
                            {{ detailText(props.attClass) }}
                            <div
                                v-if="attClassDetail"
                                data-test="ten-dlc-att-class-detail"
                                class="font-normal text-slate-500"
                            >
                                {{ attClassDetail }}
                            </div>
                        </dd>
                    </dl>
                </div>
            </template>
        </vx-popover>
    </div>
    <vx-popover v-else-if="props.status === 'attach_failed'" hover teleport arrow placement="top">
        <vx-badge data-test="ten-dlc-campaign-status" color="rose" size="xs">
            {{ tenDlcCampaignStatusLabel(props.status) }}
        </vx-badge>
        <template v-slot:content>
            <div class="flex max-w-[280px] flex-col items-start gap-2">
                <vx-badge data-test="ten-dlc-popover-status" color="rose" size="xs">
                    {{ tenDlcCampaignStatusLabel(props.status) }}
                </vx-badge>
                <b>The last attach order did not take</b>
                <dl class="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                    <template v-if="props.attachOrderStatus !== ''">
                        <dt class="text-slate-500">Order</dt>
                        <dd data-test="ten-dlc-attach-order-status" class="m-0 font-semibold">
                            {{ phoneNumberOrderStatusLabels[props.attachOrderStatus] }}
                        </dd>
                    </template>
                    <template v-if="props.attachOrderPlacedAt !== ''">
                        <dt class="text-slate-500">Placed</dt>
                        <dd data-test="ten-dlc-attach-order-placed" class="m-0 font-semibold">
                            {{ fromNow(props.attachOrderPlacedAt) }}
                        </dd>
                    </template>
                    <template v-if="attachOrderError !== ''">
                        <dt class="text-slate-500">Error</dt>
                        <dd data-test="ten-dlc-attach-order-error" class="m-0 font-semibold text-rose-600">
                            {{ attachOrderError }}
                        </dd>
                    </template>
                </dl>
            </div>
        </template>
    </vx-popover>
    <span v-else data-test="ten-dlc-campaign" class="text-slate-500">{{
        tenDlcCampaignStatusLabel(props.status)
    }}</span>
</template>

<script lang="ts" setup>
import { VxBadge, VxPopover, type VxBadgeColor } from '@voxie/frontend-components';
import { computed } from 'vue';
import type { KioskTeamGroupTenDlcCampaignStatus } from '~/services/adminTeamGroupsClient.types';
import { messageClassDetail, verificationLabel } from '~/components/kiosk/kiosk-ten-dlc/tenDlcLabels';
import { phoneNumberOrderStatusLabels } from '~/components/kiosk/kiosk-ten-dlc/registration/tenDlcRegistrationLabels';
import type { TcrPhoneNumberOrderStatus } from '~/services/adminTenDlcRegistrationClient.types';
import { fromNow } from '~/utils/date';
import { tenDlcCampaignStatusLabel, useCaseLabel } from './kioskTeamGroupsTableHelpers';

const statusColors: Partial<Record<KioskTeamGroupTenDlcCampaignStatus, VxBadgeColor>> = {
    active: 'sky',
    pending: 'amber',
    failed: 'rose',
};

const props = withDefaults(
    defineProps<{
        status: KioskTeamGroupTenDlcCampaignStatus;
        campaignId: string;
        label: string;
        brandVerified: boolean | null;
        vettingScore: number | null;
        useCase: string;
        attClass: string;
        attachOrderStatus?: TcrPhoneNumberOrderStatus | '';
        attachOrderError?: string;
        attachOrderErrorCode?: number | null;
        attachOrderPlacedAt?: string;
    }>(),
    {
        attachOrderStatus: '',
        attachOrderError: '',
        attachOrderErrorCode: null,
        attachOrderPlacedAt: '',
    }
);

const statusColor = computed(() => statusColors[props.status] ?? 'slate');

const campaignLabel = computed(() => (props.label === '' ? props.campaignId : props.label));

const brandTrust = computed(() =>
    props.brandVerified === null ? '' : verificationLabel(props.brandVerified, props.vettingScore)
);

const attClassDetail = computed(() => (props.attClass === '' ? null : messageClassDetail(props.attClass)));

const attachOrderError = computed(() => {
    const description = props.attachOrderError;
    const code = props.attachOrderErrorCode;

    if (code === null) {
        return description;
    }

    return description === '' ? `Error ${code}` : `${description} (error ${code})`;
});

const detailText = (value: string): string => (value === '' ? '-' : value);
</script>
